import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { isoDate, shiftIsoDate } from "../format";
import { Label } from "@/components/ui/label";
import { DateRangePicker } from "./DateRangePicker";

/** Khoảng ngày nghiệp vụ, hai đầu đều tính, `YYYY-MM-DD` theo giờ địa phương. */
export interface DateRange {
  from: string;
  to: string;
}

export type RangePreset = "today" | "7d" | "30d" | "month" | "quarter";

export const RANGE_PRESETS: { key: RangePreset; label: string }[] = [
  { key: "today", label: "Hôm nay" },
  { key: "7d", label: "7 ngày" },
  { key: "30d", label: "30 ngày" },
  { key: "month", label: "Tháng này" },
  { key: "quarter", label: "Quý này" },
];

/**
 * Khoảng của một preset tính tại `now`, theo GIỜ ĐỊA PHƯƠNG (#19847: không dùng `toISOString`).
 * "7 ngày" = hôm nay và 6 ngày trước — bảy ngày, tính cả hôm nay. "Tháng này" / "Quý này" chạy
 * từ ngày đầu kỳ tới HÔM NAY, không tới cuối kỳ: phần chưa tới không có số nào để đếm.
 */
export function presetRange(preset: RangePreset, now: Date = new Date()): DateRange {
  const today = isoDate(now);
  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "7d":
      return { from: shiftIsoDate(now, -6), to: today };
    case "30d":
      return { from: shiftIsoDate(now, -29), to: today };
    case "month":
      return { from: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
    case "quarter": {
      const q = Math.floor(now.getMonth() / 3) * 3;
      return { from: isoDate(new Date(now.getFullYear(), q, 1)), to: today };
    }
  }
}

/** Preset nào cho đúng khoảng này (để tô nút đang chọn); không khớp preset nào → null. */
export function matchPreset(r: DateRange, now: Date = new Date()): RangePreset | null {
  for (const p of RANGE_PRESETS) {
    const x = presetRange(p.key, now);
    if (x.from === r.from && x.to === r.to) return p.key;
  }
  return null;
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** Số ngày của một khoảng, tính cả hai đầu (theo lịch, không theo giờ — không lệch khi đổi giờ). */
export function rangeDayCount(r: DateRange): number {
  const day = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    return Date.UTC(y, m - 1, d) / 86_400_000;
  };
  return day(r.to) - day(r.from) + 1;
}

/**
 * Khoảng ngày đọc/ghi trên URL (`?from=&to=`), để một báo cáo đang xem gửi được cho người khác và
 * nút Back đưa về đúng khoảng trước. URL thiếu hoặc sai (định dạng, `from > to`) → `fallback`.
 */
export function useDateRangeParam(fallback: RangePreset = "30d") {
  const [params, setParams] = useSearchParams();
  const from = params.get("from");
  const to = params.get("to");
  const valid = !!from && !!to && ISO.test(from) && ISO.test(to) && from <= to;
  const range: DateRange = valid ? { from: from!, to: to! } : presetRange(fallback);
  const setRange = (r: DateRange) => {
    const next = new URLSearchParams(params);
    next.set("from", r.from);
    next.set("to", r.to);
    setParams(next, { replace: true });
  };
  return { range, setRange };
}

/**
 * Bộ lọc khoảng ngày dùng chung của các trang thống kê (#19860 / STAT-23).
 *
 * Giao diện là `DateRangePicker` kiểu SalePro (một ô `dd/MM/yyyy → dd/MM/yyyy` mở lịch một tháng +
 * preset ở chân), cùng bộ chọn với các màn danh sách (Lịch hẹn, Hoá đơn, Đơn thuốc…) — trước đây
 * báo cáo mang một dãy nút preset riêng + hai ô `type="date"` của trình duyệt, nên cùng một việc
 * "chọn khoảng ngày" trông khác nhau ở hai nửa sản phẩm. Phần báo cáo giữ lại chỉ là luật: khoảng
 * nằm trên URL (`useDateRangeParam`), và trần độ dài `maxDays` báo lỗi tại chỗ thay vì gửi đi để
 * nhận 400 — server vẫn kiểm (ReportWindow), đây chỉ là để người dùng không phải đợi một vòng mạng.
 *
 * Bộ chọn luôn trả `from ≤ to` (hai lần bấm ngày được sắp theo lịch), nên không còn ca "Từ sau Đến".
 */
export function DateRangeFilter({
  value,
  onChange,
  idPrefix = "range",
  maxDays,
  label = "Khoảng ngày",
}: {
  value: DateRange;
  onChange: (r: DateRange) => void;
  idPrefix?: string;
  /**
   * Trần độ dài khoảng của báo cáo (#19918), cùng số với `ReportWindow` phía server. Khoảng dài hơn
   * báo lỗi ngay tại chỗ và không được gửi đi — thay vì nhận về 400 rồi một màn lỗi chung chung.
   */
  maxDays?: number;
  /** Nhãn hiện trên ô chọn; `null` khi trang đã có nhãn riêng ngay cạnh (màn Doanh thu). */
  label?: string | null;
}) {
  // Khoảng đang hiện trên ô chọn. Khác `value` đúng một trường hợp: người dùng vừa chọn một khoảng
  // dài hơn trần — ô hiện khoảng họ vừa chọn kèm lỗi, còn số liệu vẫn theo khoảng hợp lệ trước đó.
  const [draft, setDraft] = useState<DateRange>(value);
  // Khoảng đổi từ BÊN NGOÀI (Back/Forward, URL được sửa): ô chọn đi theo nó, thay vì giữ ngày cũ
  // trong khi số liệu đã theo URL (review #19860).
  useEffect(() => {
    setDraft((d) => (d.from === value.from && d.to === value.to ? d : value));
  }, [value.from, value.to]); // eslint-disable-line react-hooks/exhaustive-deps
  const tooLong = (r: DateRange) => !!maxDays && !!r.from && !!r.to && rangeDayCount(r) > maxDays;
  const draftTooLong = tooLong(draft);

  const commit = (r: DateRange) => {
    setDraft(r);
    if (r.from && r.to && r.from <= r.to && !tooLong(r)) onChange(r);
  };

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="space-y-1.5">
        {label && (
          <Label htmlFor={idPrefix} className="text-xs text-muted-foreground">
            {label}
          </Label>
        )}
        <DateRangePicker
          id={idPrefix}
          className="w-[260px]"
          from={draft.from}
          to={draft.to}
          onChange={commit}
          allowClear={false}
        />
      </div>
      {draftTooLong && (
        <p role="alert" className="pb-2 text-xs text-destructive">
          Khoảng ngày tối đa của báo cáo này là {maxDays} ngày (đang chọn {rangeDayCount(draft)} ngày).
        </p>
      )}
    </div>
  );
}
